import { useLazyQuery, useMutation } from "@apollo/client";
import React, { useState, useEffect } from "react";
import { clientPolicyIngsight } from "../../App";
import PolicyQueueListComponent from "../../components/PolicyQueueListComponent/PolicyQueueListComponent";
import { GET_QUEUES_ADMIN } from "../../GraphQL/Queries";
import { GeneralContainer, useStyles } from "./QueueListContainer.styled";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";
import ModalLoader from "../../components/ModalLoader/ModalLoader";
import { FormControlLabel, IconButton } from "@material-ui/core";
import { DeleteTwoTone, EditTwoTone } from "@material-ui/icons";
import { CHANGE_QUEUE_STATUS } from "../../GraphQL/Mutations";
import { NotificationManager } from "react-notifications";
import ModalConfirmAction from "../../components/ModalConfirmAction/ModalConfirmAction";
import { AntSwitch } from "../../helpers/antSwitch";
import { theme as themeConfig } from "../../themes/themes";

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: themeConfig.neutral.gray[2500],
    margin: "9 0",
    color: "white",
    border: "1px solid #dadde9",
  },
}))(Tooltip);

const QueueListContainer = ({
  renderAgain,
  setRenderAgain,
  searchText,
  handleEdit,
}) => {
  const [data, setData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const classes = useStyles();
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 20];
  const [page, setPage] = useState(0);
  const [allPages, setAllPages] = useState(0);
  const [startCursor, setStartCursor] = useState("");
  const [endCursor, setEndCursor] = useState("");
  const [changeStatus] = useMutation(CHANGE_QUEUE_STATUS, {
    client: clientPolicyIngsight,
  });
  const [openDeleteModal, setOpenDeleteModal] = useState();
  const columns = [
    {
      title: "ID",
      field: "idQueue",
      type: "string",
    },
    {
      title: "Queue",
      field: "queueName",
      type: "string",
    },
    {
      title: "LOB",
      field: "lobs",
      render: (rowData) => (
        <>
          <HtmlTooltip
            arrow
            placement="right"
            title={
              <>
                <ul
                  style={{
                    padding: "1em 0em 1em 1.5em",
                    margin: "0",
                    listStyleType: "circle",
                    lineHeight: "1.3",
                  }}
                >
                  {rowData?.lobs?.map((l, i) => (
                    <li key={i} style={{ fontSize: "11px" }}>
                      {l.name}
                    </li>
                  ))}
                </ul>
              </>
            }
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  backgroundColor: themeConfig.primary.purple,
                  borderRadius: "99px",
                  width: "22px",
                  height: "22px",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                  color: "white",
                }}
              >
                {rowData?.lobs?.length}
              </div>
            </div>
          </HtmlTooltip>
        </>
      ),
    },
    {
      title: "Created By",
      field: "createUserInfo",
      type: "string",
      render: (rowData) => rowData?.createUserInfo?.emailAddress,
    },
    {
      title: "Created Date",
      field: "createDate",
      type: "string",
      render: (rowData) => moment(rowData.createDate).format("YYYY-MM-DD"),
    },
    {
      title: "Disable",
      field: "recordStatus",
      cellStyle: {
        padding: "5px 35px",
      },
      render: (rowData) =>
        rowData && (
          <FormControlLabel
            control={
              <AntSwitch
                size="small"
                checked={rowData?.recordStatus === "Active" ? true : false}
                onChange={() => {
                  changeState(rowData);
                  rowData["recordStatus"] =
                    rowData.recordStatus === "Active" ? "Inactive" : "Active";
                }}
              />
            }
          />
        ),
    },
    {
      title: "Actions",
      field: "internal_action",
      sorting: false,
      render: (rowData) =>
        rowData && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 0,
            }}
          >
            <Tooltip title="Edit">
              <IconButton
                className={classes.delete}
                onClick={() => handleEdit(rowData)}
              >
                <EditTwoTone style={{ color: themeConfig.text.primary }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                disabled={rowData.recordStatus === "Active"}
                className={classes.delete}
                onClick={() => setOpenDeleteModal(rowData)}
              >
                <DeleteTwoTone
                  style={{
                    color: themeConfig.text.primary,
                    opacity: rowData.recordStatus === "Active" ? 0.5 : 1,
                  }}
                />
              </IconButton>
            </Tooltip>
          </div>
        ),
    },
  ];

  const handleChangeRowsPerPage = (item) => {
    let value = item.target.value ? parseInt(item.target.value) : 5;
    setRowsPerPage(value);
    setPage(0);
    runQueryGetQueues({
      variables: {
        first: value,
        search: "",
      },
    });
  };

  const handleChangePage = (item, newPage) => {
    if (newPage > page) {
      setShowLoader(true);
      runQueryGetQueues({
        variables: {
          first: rowsPerPage,
          after: endCursor,
          search: "",
        },
      });
    }
    if (newPage < page) {
      setShowLoader(true);
      runQueryGetQueues({
        variables: {
          first: rowsPerPage,
          before: startCursor,
          search: "",
        },
      });
    }
    setPage(newPage);
  };

  const changeState = ({ idQueue, recordStatus }) => {
    setShowLoader(true);
    changeStatus({
      variables: {
        queueId: idQueue,
        newQueueStatus: recordStatus === "Active" ? "INACTIVE" : "ACTIVE",
      },
    })
      .then((res) => {
        if (res?.data?.changeStatusQueue?.idQueue) {
          NotificationManager.success("Queue status updated successfully");
        }
        setShowLoader(false);
      })
      .catch((error) => {
        NotificationManager.error(error.message, "", 5000);

        setShowLoader(false);
      });
  };

  const handleDelete = () => {
    deleteHandler(openDeleteModal);
    setOpenDeleteModal(null);
  };

  const deleteHandler = ({ idQueue }) => {
    setShowLoader(true);
    changeStatus({
      variables: {
        queueId: idQueue,
        newQueueStatus: "DELETED",
      },
    })
      .then((res) => {
        if (res?.data?.changeStatusQueue?.idQueue) {
          NotificationManager.success("Queue deleted successfully");
          setRenderAgain(!renderAgain);
        }
        setShowLoader(false);
      })
      .catch((error) => {
        NotificationManager.error(error.message, "", 5000);

        setShowLoader(false);
      });
  };

  const showDeleteModal = () => {
    return openDeleteModal ? (
      <ModalConfirmAction
        title={"Are you sure you want to delete this queue?"}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        fn={handleDelete}
      />
    ) : null;
  };

  const [runQueryGetQueues] = useLazyQuery(GET_QUEUES_ADMIN, {
    fetchPolicy: "network-only",
    client: clientPolicyIngsight,
    onCompleted: (data) => {
      if (data?.queues?.nodes) {
        setData([...data.queues.nodes]);
        setAllPages(data.queues.totalCount);
        if (data.queues.pageInfo.hasNextPage === true) {
          setEndCursor(data.queues.pageInfo.endCursor);
        }
        if (data.queues.pageInfo.hasPreviousPage === true) {
          setStartCursor(data.queues.pageInfo.startCursor);
        }
        setShowLoader(false);
      }
    },
  });

  useEffect(() => {
    setShowLoader(true);
    runQueryGetQueues({
      variables: {
        first: rowsPerPage,
        search: searchText,
      },
    });
  }, [renderAgain, searchText]);

  return (
    <GeneralContainer>
      <PolicyQueueListComponent
        data={data}
        columns={columns}
        page={page}
        allPages={allPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        selectedMenu={"queue"}
      />
      {showLoader && <ModalLoader />}
      {showDeleteModal()}
    </GeneralContainer>
  );
};

export default QueueListContainer;
